import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import { resolveActiveBusiness } from '@data/services/businessSession';
import {
  createParty,
  deleteParty,
  updateParty,
  type CreatePartyInput,
  type PartyWriteResult,
} from '@data/services/partyService';
import { PARTIES_QUERY_ROOT } from './useParties';
import { MASTER_DATA_DEPENDENT_ROOTS } from '@features/purchaseOrders/usePurchaseOrders';
import type { Party } from '@data/db/types';

/**
 * Writing customers and suppliers.
 *
 * EVERY WRITE OPENS ITS OWN TRANSACTION, and every write names the device
 * explicitly. The services are written to take a live transaction rather than
 * opening one themselves — that is what lets an existing caller compose them with
 * other writes — so the screen is where the scope is declared. `syncOutbox` is in
 * scope for every one: a party written without publishing its change reaches this
 * device and no other, which is a customer who exists on the till and nowhere
 * else.
 */
const PARTY_WRITE_SCOPE = ['parties', 'syncOutbox'] as const;

/**
 * Refreshing only the contact lists is not enough.
 *
 * Renaming or deactivating a party changes what every document editor offers as a
 * counterparty, and those editors cache their options under their OWN query roots.
 * Stopping the invalidation at `PARTIES_QUERY_ROOT` would leave an open Purchase
 * Order listing a supplier who no longer exists.
 *
 * The same shared list as the product writer uses, so there is one definition of
 * "which screens depend on master data" rather than one per writer.
 */
async function invalidateParties(client: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    client.invalidateQueries({ queryKey: PARTIES_QUERY_ROOT }),
    ...MASTER_DATA_DEPENDENT_ROOTS.map((queryKey) => client.invalidateQueries({ queryKey })),
  ]);
}

export interface UseCreatePartyOptions {
  readonly onDone?: () => void;
  /** Fires when the service finds a soft-duplicate (same name+phone+email). */
  readonly onWarning?: (duplicate: Party) => void;
}

export function useCreateParty({ onDone, onWarning }: UseCreatePartyOptions = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Omit<CreatePartyInput, 'businessId' | 'user' | 'now' | 'deviceId'> & {
        force?: boolean;
      },
    ) => {
      const { force, ...cleanInput } = input;
      const db = getDb();
      const business = await resolveActiveBusiness(db);
      const device = await currentDeviceId(db);
      return db.transaction(
        'rw',
        PARTY_WRITE_SCOPE.map((n) => db.table(n)),
        () =>
          createParty(
            db as never,
            {
              ...cleanInput,
              businessId: business.id,
              user: { id: 'local', name: 'You' },
              now: new Date().toISOString(),
              deviceId: device,
            },
            force ?? false,
          ),
      );
    },
    onSuccess: async (result: PartyWriteResult) => {
      await invalidateParties(client);
      if (result.party) onDone?.();
      if (result.potentialDuplicate) onWarning?.(result.potentialDuplicate);
    },
  });
}

export function useUpdateParty({ onDone, onWarning }: UseCreatePartyOptions = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
      force,
    }: {
      id: string;
      input: Partial<Omit<CreatePartyInput, 'user' | 'now' | 'deviceId'>>;
      force?: boolean;
    }) => {
      const db = getDb();
      const business = await resolveActiveBusiness(db);
      const device = await currentDeviceId(db);
      return db.transaction(
        'rw',
        PARTY_WRITE_SCOPE.map((n) => db.table(n)),
        () =>
          updateParty(
            db as never,
            {
              ...input,
              id,
              businessId: business.id,
              user: { id: 'local', name: 'You' },
              now: new Date().toISOString(),
              deviceId: device,
            },
            force ?? false,
          ),
      );
    },
    onSuccess: async (result: PartyWriteResult) => {
      await invalidateParties(client);
      if (result.party) onDone?.();
      if (result.potentialDuplicate) onWarning?.(result.potentialDuplicate);
    },
  });
}

/**
 * Deactivate a customer. NOT a hard delete — see the service header: a document
 * that referenced this customer must still be able to print the name and GSTIN
 * that were on it when issued, and an offline device must not be able to
 * resurrect the row.
 */
export function useDeactivateParty({ onDone }: UseCreatePartyOptions = {}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const db = getDb();
      const business = await resolveActiveBusiness(db);
      const device = await currentDeviceId(db);
      return db.transaction(
        'rw',
        PARTY_WRITE_SCOPE.map((n) => db.table(n)),
        () =>
          deleteParty(db as never, {
            id,
            businessId: business.id,
            now: new Date().toISOString(),
            deviceId: device,
          }),
      );
    },
    onSuccess: async () => {
      await invalidateParties(client);
      onDone?.();
    },
  });
}
